/**
 * Chrome Built-in AI (Gemini Nano) detection + session wrapper.
 *
 * Feature-detects two API generations:
 *   - modern: globalThis.LanguageModel.availability() / .create()    (Chrome 138+)
 *   - legacy: window.ai.canCreateTextSession() / .createTextSession() (earlier origin trial)
 *
 * If neither is present the functions return null / throw — the provider
 * simply never appears in the picker so non-Chrome browsers see no change.
 */
import type { ProviderInfo } from '@/stores/aiProviderStore'

export const BROWSER_PROVIDER_ID = 'browser-gemini-nano'

type AvailabilityStatus =
  | 'no'
  | 'readily'
  | 'after-download'
  | 'available'
  | 'downloadable'
  | 'downloading'
  | 'unavailable'

interface LegacySession {
  prompt: (input: string) => Promise<string>
  destroy?: () => void
}

interface LegacyAI {
  canCreateTextSession?: () => Promise<AvailabilityStatus>
  createTextSession?: (opts?: {
    temperature?: number
    topK?: number
    systemPrompt?: string
  }) => Promise<LegacySession>
}

interface ModernSession {
  prompt: (input: string) => Promise<string>
  destroy?: () => void
}

interface ModernLanguageModel {
  availability: (opts?: Record<string, unknown>) => Promise<AvailabilityStatus>
  create: (opts?: {
    temperature?: number
    topK?: number
    initialPrompts?: Array<{ role: string; content: string }>
  }) => Promise<ModernSession>
}

export interface BrowserAISession {
  prompt: (input: string) => Promise<string>
  destroy: () => void
}

function getLegacyAI(): LegacyAI | null {
  try {
    const ai = (globalThis as unknown as { ai?: LegacyAI }).ai
    return ai && typeof ai.canCreateTextSession === 'function' ? ai : null
  } catch {
    return null
  }
}

function getModernLM(): ModernLanguageModel | null {
  try {
    const lm = (globalThis as unknown as { LanguageModel?: ModernLanguageModel }).LanguageModel
    return lm && typeof lm.availability === 'function' ? lm : null
  } catch {
    return null
  }
}

function isUsable(status: AvailabilityStatus): boolean {
  return (
    status === 'readily' ||
    status === 'available' ||
    status === 'after-download' ||
    status === 'downloadable'
  )
}

export async function detectBrowserAI(): Promise<ProviderInfo | null> {
  const modern = getModernLM()
  if (modern) {
    try {
      const status = await modern.availability()
      if (status === 'unavailable' || status === 'no') return null
      return {
        id: BROWSER_PROVIDER_ID,
        name: 'Chrome Built-in AI',
        available: isUsable(status),
        model: 'gemini-nano',
        detail:
          status === 'readily' || status === 'available'
            ? 'On-device · zero cost · no data leaves your browser'
            : 'Available after first model download',
      }
    } catch {
      /* fall through to legacy */
    }
  }

  const legacy = getLegacyAI()
  if (legacy?.canCreateTextSession) {
    try {
      const status = await legacy.canCreateTextSession()
      if (status === 'no' || status === 'unavailable') return null
      return {
        id: BROWSER_PROVIDER_ID,
        name: 'Chrome Built-in AI',
        available: isUsable(status),
        model: 'gemini-nano',
        detail: 'On-device · zero cost · experimental',
      }
    } catch {
      /* fall through */
    }
  }

  return null
}

export async function createBrowserSession(systemPrompt: string): Promise<BrowserAISession> {
  const modern = getModernLM()
  if (modern) {
    const session = await modern.create({
      temperature: 0.7,
      topK: 3,
      initialPrompts: systemPrompt ? [{ role: 'system', content: systemPrompt }] : undefined,
    })
    return {
      prompt: (t) => session.prompt(t),
      destroy: () => {
        try {
          session.destroy?.()
        } catch {
          /* noop */
        }
      },
    }
  }

  const legacy = getLegacyAI()
  if (legacy?.createTextSession) {
    const session = await legacy.createTextSession({
      temperature: 0.7,
      topK: 3,
      systemPrompt,
    })
    return {
      prompt: (t) => session.prompt(t),
      destroy: () => {
        try {
          session.destroy?.()
        } catch {
          /* noop */
        }
      },
    }
  }

  throw new Error(
    'Chrome Built-in AI not available — enable chrome://flags/#prompt-api-for-gemini-nano and reload.'
  )
}
